'use client';

import React from 'react';
import { motion } from 'framer-motion';

export interface HeroGlobeBandProps {
  /** Small category / eyebrow pill text (optional) */
  badge?: React.ReactNode;
  /** Main hero heading */
  title: React.ReactNode;
  /** Subtitle / descriptive paragraph */
  subtitle?: React.ReactNode;
  /** Optional CTA buttons or actions */
  actions?: React.ReactNode;
  /** Custom extra children (e.g. search bar, filter tabs, audience switcher) */
  children?: React.ReactNode;
  /** Optional container class name override */
  className?: string;
  /** Optional theme override ('light' nature palette by default, or 'dark' for API docs) */
  theme?: 'nature' | 'dark';
}

export function HeroGlobeBand({
  badge,
  title,
  subtitle,
  actions,
  children,
  className = '',
  theme = 'nature',
}: HeroGlobeBandProps) {
  const isDark = theme === 'dark';

  return (
    <section
      className={`relative w-full overflow-visible bg-transparent ${
        isDark ? 'text-text-inverse' : 'text-[#2D3536]'
      } ${className}`}
    >
      {/* Topographic & Dot-Grid Ambient Texture */}
      <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden opacity-[0.065]">
        <svg className="h-full w-full" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
          <defs>
            <pattern id="hero-globe-dot-grid" width="32" height="32" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1.2" fill={isDark ? '#FFFFFF' : '#2D3536'} />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#hero-globe-dot-grid)" />
          <path
            d="M-100 150 C 200 80, 400 250, 700 180 S 1100 80, 1400 220 S 1800 120, 2100 260"
            fill="none"
            stroke={isDark ? '#FFFFFF' : '#2D3536'}
            strokeWidth="1.5"
          />
          <path
            d="M-100 280 C 250 200, 450 380, 780 300 S 1200 200, 1500 340 S 1850 240, 2100 380"
            fill="none"
            stroke={isDark ? '#FFFFFF' : '#2D3536'}
            strokeWidth="1.5"
          />
          <path
            d="M-100 420 C 300 320, 500 500, 850 420 S 1250 320, 1600 460 S 1900 360, 2100 500"
            fill="none"
            stroke={isDark ? '#FFFFFF' : '#2D3536'}
            strokeWidth="1.5"
          />
        </svg>
      </div>



      {/* 5. Header Content (Carefully offset below the fixed 80px / h-20 navbar) */}
      <div className="relative z-10 w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 sm:pt-32 md:pt-36 pb-12 sm:pb-16 flex flex-col items-center justify-center text-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
          className="flex flex-col items-center justify-center w-full"
        >
          {badge && (
            <div className="mb-3 sm:mb-4 inline-flex items-center gap-1.5 rounded-full px-3.5 py-1 text-xs font-semibold uppercase tracking-[0.16em] bg-bg/75 backdrop-blur-xs border border-[#2D3536]/10 text-[#55695b] shadow-xs">
              {badge}
            </div>
          )}

          <h1
            className={`text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] font-bold tracking-tight leading-tight ${
              isDark ? 'text-text-inverse' : 'text-[#2D3536]'
            }`}
            style={{ fontFamily: "'Cormorant Garamond', 'Plus Jakarta Sans', serif" }}
          >
            {title}
          </h1>

          {subtitle && (
            <p
              className={`mt-4 sm:mt-5 max-w-2xl text-base sm:text-lg leading-relaxed ${
                isDark ? 'text-slate-300' : 'text-[#2D3536]/80'
              }`}
            >
              {subtitle}
            </p>
          )}

          {actions && (
            <div className="mt-7 sm:mt-8 flex flex-wrap items-center justify-center gap-3 sm:gap-4">
              {actions}
            </div>
          )}

          {children && <div className="mt-6 sm:mt-8 w-full">{children}</div>}
        </motion.div>
      </div>
    </section>
  );
}
